/**
 * THIS IS A CUSTOM BANNER WITH A FLOWBITE-REACT CARD
 * @param {string} title - Title of the banner
 * @param {string} subtitle - Subtitle of the banner
 */

import { Card } from "flowbite-react";
import { lucideIconRegistry } from "yeati-ui";
import type { Icon3dVariant } from "@/features/auth/types/organization.type";
import { Breadcrumb, type BreadcrumbItem } from "@/shared/components/breadcrumb";
import { useBannerIcon } from "@/shared/hooks/useBannerIcon";
import { H1 } from "@/shared/ui/typographies/h1";
import { H4 } from "@/shared/ui/typographies/h4";
import { darkenHex } from "@/shared/utils/brandColors";

type BannerProps = {
  title: string
  subtitle?: string
  breadcrumbs?: BreadcrumbItem[]
  context?: "success" | "error" | "warning"
  icon?: string
  hideIcon?: boolean
  pageKey?: string
  titleLevel?: "h1" | "h4"
  actions?: React.ReactNode
  children?: React.ReactNode
};

const DEFAULT_3D_VARIANT: Icon3dVariant = "color";

const get3dIconPath = (name: string, variant?: Icon3dVariant): string =>
  `/icons/3d/${variant ?? DEFAULT_3D_VARIANT}/${name}.webp`;

export const PageBanner = ({
  title, subtitle, breadcrumbs, context, icon, hideIcon, pageKey, titleLevel = "h4", actions, children,
}: BannerProps) => {
  const TitleHeading = titleLevel === "h1" ? H1 : H4;
  const bannerIconConfig = useBannerIcon(pageKey);

  const getBannerConfig = (context: "success" | "error" | "warning" | "info" = "info") => {
    if (context === "success") return {
      bg: "bg-gradient-to-r from-emerald-500 to-emerald-400",
      text: "text-white",
      icon: "success.png",
    };

    if (context === "error") return {
      bg: "bg-gradient-to-r from-red-200 to-red-300",
      text: "text-red-950",
      icon: "failure.png",
    };

    if (context === "warning") return {
      bg: "bg-gradient-to-r from-red-200 to-red-300",
      text: "text-orange-950",
      icon: "warning.png",
    };

    return {
      bg: "bg-(--color-lightprimary)",
      text: "!text-darkprimary dark:!text-white",
      icon: "ChatBc.png",
    };
  };

  const bannerConfig = getBannerConfig(context);

  const iconType = bannerIconConfig?.iconType ?? "lucide";
  const isCustomIcon = !!bannerIconConfig && !context;

  const LucideIcon = (isCustomIcon && iconType === "lucide"
    ? lucideIconRegistry[bannerIconConfig.icon]
    : undefined) as React.ComponentType<{
      "size"?: number
      "className"?: string
      "strokeWidth"?: number
      "style"?: React.CSSProperties
      "aria-hidden"?: boolean
    }> | undefined;

  const is3dIcon = isCustomIcon && iconType === "3d";
  const isEmojiIcon = isCustomIcon && iconType === "emoji";
  const hasCustomIcon = !!LucideIcon || is3dIcon || isEmojiIcon;

  const iconRotation = bannerIconConfig?.iconRotation ?? 5;
  const customTextColor = isCustomIcon ? (bannerIconConfig.textColor || "#FFFFFF") : undefined;
  const iconRotateStyle: React.CSSProperties = { transform: `rotate(${iconRotation}deg)` };

  const hasCustomColors = isCustomIcon && !!bannerIconConfig.color1;
  const isGradientDisabled = isCustomIcon && bannerIconConfig.gradient === false;
  const hasBackgroundImage = isCustomIcon && !!bannerIconConfig.backgroundImage;
  const shouldShowIcon = !hideIcon && (!hasBackgroundImage || bannerIconConfig.showIcon !== false);
  const overlayOpacity = hasBackgroundImage ? (bannerIconConfig.overlayOpacity ?? 0) : 0;

  const customBgStyle = (() => {
    if (!isCustomIcon) return undefined;
    if (hasBackgroundImage) {
      return {
        backgroundImage: `url(${bannerIconConfig.backgroundImage})`,
        backgroundSize: "cover" as const,
        backgroundPosition: "center" as const,
      };
    }
    if (hasCustomColors) {
      const c1 = bannerIconConfig.color1!;
      if (isGradientDisabled) return { background: c1 };
      const c2 = bannerIconConfig.color2 || darkenHex(c1, 0.7);
      return { background: `linear-gradient(to right, ${c1}, ${c2})` };
    }
    return undefined;
  })();

  const bgClass = customBgStyle
    ? ""
    : hasCustomIcon
      ? isGradientDisabled
        ? "bg-(--brand-primary)"
        : "bg-linear-to-r from-(--brand-primary) to-[color-mix(in_srgb,var(--brand-primary),black_30%)]"
      : bannerConfig.bg;

  const showLucideIcon = shouldShowIcon && !!LucideIcon;
  const show3dIcon = shouldShowIcon && is3dIcon;
  const showEmojiIcon = shouldShowIcon && isEmojiIcon;
  const showDefaultIcon = shouldShowIcon && !LucideIcon && !is3dIcon && !isEmojiIcon;

  return (
    <Card className={`relative py-4 px-6 ${bgClass} dark:bg-(--color-darkprimary) overflow-hidden rounded-md border-none shadow-none dark:shadow-none`} style={customBgStyle}>
      {overlayOpacity > 0 && (
        <div
          className="absolute inset-0"
          style={{
            backdropFilter: `blur(${overlayOpacity * 0.1}px)`,
            backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 200})`,
          }}
        />
      )}
      {actions && <div className="absolute right-4 top-4 z-20">{actions}</div>}
      <div className="items-center grid grid-cols-12 gap-4 md:gap-6">
        <div className="col-span-12 md:col-span-9 z-10">
          {/* Breadcrumb */}
          <Breadcrumb items={breadcrumbs || []} className={actions ? "pr-36" : ""} />

          {/* Title */}
          <TitleHeading
            className={`mb-3 break-words !text-[2rem] ${customTextColor ? "" : hasCustomIcon || hasBackgroundImage ? "!text-white" : bannerConfig.text}`}
            style={customTextColor ? { color: customTextColor } : undefined}
          >
            {title}
          </TitleHeading>

          {/* Subtitle */}
          {subtitle && (
            <ol
              className={`flex items-center break-words text-foreground ${customTextColor ? "" : hasCustomIcon || hasBackgroundImage ? "!text-white" : bannerConfig.text}`}
              style={customTextColor ? { color: customTextColor } : undefined}
              aria-label="Breadcrumb"
            >
              <li className="flex items-center">
                {subtitle}
              </li>
            </ol>
          )}

          {children && <div className="mt-5">{children}</div>}
        </div>
        {showLucideIcon
          ? (
            <div className="col-span-12 md:col-span-3 hidden md:flex relative justify-center items-center z-0 opacity-50 md:opacity-100">
              <LucideIcon
                size={120}
                className="absolute text-white"
                strokeWidth={1.5}
                style={{ ...iconRotateStyle, ...(customTextColor ? { color: customTextColor } : {}) }}
                aria-hidden
              />
            </div>
          )
          : show3dIcon
            ? (
              <div className="col-span-12 md:col-span-3 hidden md:flex relative justify-center items-center z-0 md:opacity-100">
                <img
                  src={get3dIconPath(bannerIconConfig.icon, bannerIconConfig.iconVariant)}
                  alt=""
                  className="absolute h-28 w-28 object-contain drop-shadow-lg"
                  style={iconRotateStyle}
                  loading="lazy"
                  aria-hidden
                />
              </div>
            )
            : showEmojiIcon
              ? (
                <div className="col-span-12 md:col-span-3 hidden md:flex relative justify-center items-center z-0 md:opacity-100">
                  <span
                    className="absolute text-[7rem] leading-none drop-shadow-lg select-none"
                    style={iconRotateStyle}
                    role="img"
                    aria-hidden
                  >
                    {bannerIconConfig.icon}
                  </span>
                </div>
              )
              : showDefaultIcon
                ? (
                  <div className="col-span-12 md:col-span-3 hidden md:flex justify-center -mb-10 z-0 opacity-50 md:opacity-100">
                    <img
                      src={`/images/banner/${icon || bannerConfig.icon}`}
                      alt="Illustration de la bannière"
                      className="md:-mb-[31px] -mb-8 max-w-44"
                    />
                  </div>
                )
                : null}
      </div>
    </Card>
  );
};
